import { useEffect, useRef, useState } from "react";

const turnedDownItems = [
  { icon: "ri-home-4-line", label: "A Home" },
  { icon: "ri-bank-line", label: "A Mortgage" },
  { icon: "ri-building-line", label: "An Apartment" },
  { icon: "ri-bank-card-line", label: "A Credit Card" },
  { icon: "ri-question-line", label: "Other??" },
];

export default function DreamsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="dreams"
      ref={sectionRef}
      className="py-16 md:py-24 bg-white"
    >
      <div className="px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold tracking-wider uppercase mb-4">
            Your Situation
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-dark-950 mb-4">
            Have You Been Turned Down For?
          </h2>
          <p className="text-dark-400 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Bad credit should not stand between you and the life you want. We have helped clients in all 50 states get approved for the things that matter most.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16">
          <div
            className={`relative rounded-xl overflow-hidden transition-all duration-700 delay-100 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="w-full h-64 md:h-80 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=stunning%20modern%20luxury%20suburban%20homes%20with%20manicured%20lawns%20and%20clean%20architecture%20at%20golden%20hour%2C%20warm%20sunset%20lighting%2C%20inviting%20neighborhood%20street%2C%20high%20quality%20real%20estate%20photography%2C%20warm%20earth%20tones&width=800&height=500&seq=home-img-001&orientation=landscape"
                alt="Beautiful homes you can qualify for after credit restoration"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-dark-950/70 via-dark-950/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h3 className="font-heading font-semibold text-xl text-white mb-1">
                Your Dream Home Awaits
              </h3>
              <p className="text-white/70 text-sm">
                Get approved for a mortgage after we clean up your credit report.
              </p>
            </div>
          </div>

          <div
            className={`relative rounded-xl overflow-hidden transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="w-full h-64 md:h-80 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=sleek%20modern%20luxury%20sports%20sedan%20car%20parked%20on%20a%20clean%20city%20street%20with%20warm%20golden%20lighting%2C%20premium%20vehicle%20photography%2C%20glossy%20paint%20reflecting%20sunset%2C%20aspirational%20automotive%20lifestyle&width=800&height=500&seq=car-img-001&orientation=landscape"
                alt="Luxury car you can finance after credit restoration"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-dark-950/70 via-dark-950/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h3 className="font-heading font-semibold text-xl text-white mb-1">
                Drive What You Deserve
              </h3>
              <p className="text-white/70 text-sm">
                Remove repos and charge-offs so you can get approved for auto financing.
              </p>
            </div>
          </div>
        </div>

        <div
          className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 transition-all duration-700 delay-300 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {turnedDownItems.map((item) => (
            <div
              key={item.label}
              className="flex flex-col items-center text-center p-5 md:p-6 rounded-xl bg-dark-50 border border-dark-100 hover:border-primary-200 hover:bg-primary-50 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center mb-3">
                <i className={`${item.icon} text-red-600 text-xl`} />
              </div>
              <span className="font-heading font-semibold text-dark-950 text-sm">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}